import { useMemo } from "react";
import { KpiCard } from "../../lib/funnel/charts";
import { SectionCardHeader } from "../../components/ChartCard";
import { fmtN, fmtPct } from "../../lib/funnel/helpers";
import { FG3 } from "../../lib/funnel/theme";
import type { FilterPillSpec } from "../../components/FilterPills";
import type { CompanyInsightsRow } from "./sql";

type Stats = {
  total: number;
  multiSeat: number;
  multiSeatTeamActivated: number;
  hasPostFounderTeammate: number;
  teammateIn14d: number;
  teammateIn30d: number;
  founderActivated: number;
  founderActivatedHasTeammate: number;
};

const DAY_MS = 86400_000;

function computeStats(rows: CompanyInsightsRow[]): Stats {
  let multiSeat = 0;
  let multiSeatTeamActivated = 0;
  let hasPostFounderTeammate = 0;
  let teammateIn14d = 0;
  let teammateIn30d = 0;
  let founderActivated = 0;
  let founderActivatedHasTeammate = 0;
  for (const r of rows) {
    const isMulti = r.current_active_seats >= 2;
    const teammateJoined = r.teammates_signed_up_after_founder >= 1;
    if (isMulti) {
      multiSeat += 1;
      if (r.non_founder_activated_count >= 1) multiSeatTeamActivated += 1;
    }
    if (teammateJoined) hasPostFounderTeammate += 1;
    if (r.first_teammate_signup_after_founder_utc) {
      const founderTs = new Date(r.founder_signup_date_utc).getTime();
      const teammateTs = new Date(r.first_teammate_signup_after_founder_utc).getTime();
      if (Number.isFinite(founderTs) && Number.isFinite(teammateTs) && teammateTs > founderTs) {
        const days = Math.floor((teammateTs - founderTs) / DAY_MS);
        if (days <= 14) teammateIn14d += 1;
        if (days <= 30) teammateIn30d += 1;
      }
    }
    if (r.founder_activated) {
      founderActivated += 1;
      if (teammateJoined) founderActivatedHasTeammate += 1;
    }
  }
  return {
    total: rows.length,
    multiSeat,
    multiSeatTeamActivated,
    hasPostFounderTeammate,
    teammateIn14d,
    teammateIn30d,
    founderActivated,
    founderActivatedHasTeammate,
  };
}

const TEAM_ACTIVATION_INFO =
  "Companies with 2+ active seats where at least one non-founder seat reached '2 summaries in 14 days'. Attribution uses meeting.created_by_user, so summaries generated by support staff still credit the adviser whose meeting it is.";
const TEAMMATE_14D_INFO =
  "Share of cohort companies where a second user (any role) signed up within 14 days of the founder. Replaces the planned 'Invite → Signup' card: public.company_invite has only ~303 rows globally and no timestamp, so we use the second-user signup as the only reliable team-expansion signal.";
const TEAMMATE_30D_INFO =
  "Same as the 14d card but with a 30-day window. The 30d - 14d gap is the slow-expanding accounts.";
const TEAMMATE_EVER_INFO =
  "Share of cohort companies that ever had a second user sign up after the founder. Loose proxy for 'someone invited a teammate', uncapped by time.";
const FOUNDER_PULL_INFO =
  "Of the founders who activated themselves (2 summaries in 14d), what share went on to have a teammate sign up after them? Filters out the 'lone wolf activates, no team follows' case.";

const FIRM_SIZE_CAVEAT =
  "Note: we only see the people who have signed up to Marloo at each firm, not how many people work at the firm in total. The expansion / activation rates below cannot be expressed as a 'penetration of the firm' figure until that data is captured reliably.";

export function CompanyInsights({
  companies,
  pagePills,
  compareCompanies,
  comparePills,
}: {
  companies: CompanyInsightsRow[];
  pagePills?: FilterPillSpec[];
  compareCompanies?: CompanyInsightsRow[] | null;
  comparePills?: FilterPillSpec[];
}) {
  const compareOn = !!compareCompanies;
  const base = useMemo(() => computeStats(companies), [companies]);
  const cmp = useMemo(() => compareCompanies ? computeStats(compareCompanies) : null, [compareCompanies]);

  const pills: FilterPillSpec[] = compareOn
    ? [
        ...(pagePills ?? []),
        { kind: "mode", label: "vs comparison" },
        ...(comparePills ?? []),
      ]
    : (pagePills ?? []);

  const safePct = (num: number, den: number) => (den === 0 ? 0 : (num / den) * 100);
  const teamActPct = safePct(base.multiSeatTeamActivated, base.multiSeat);
  const teammate14dPct = safePct(base.teammateIn14d, base.total);
  const teammate30dPct = safePct(base.teammateIn30d, base.total);
  const teammateEverPct = safePct(base.hasPostFounderTeammate, base.total);
  const founderPullPct = safePct(base.founderActivatedHasTeammate, base.founderActivated);

  const teamActPctCmp = cmp ? safePct(cmp.multiSeatTeamActivated, cmp.multiSeat) : null;
  const teammate14dPctCmp = cmp ? safePct(cmp.teammateIn14d, cmp.total) : null;
  const teammate30dPctCmp = cmp ? safePct(cmp.teammateIn30d, cmp.total) : null;
  const teammateEverPctCmp = cmp ? safePct(cmp.hasPostFounderTeammate, cmp.total) : null;
  const founderPullPctCmp = cmp ? safePct(cmp.founderActivatedHasTeammate, cmp.founderActivated) : null;

  return (
    <section className="card">
      <SectionCardHeader
        title="Account / team insights"
        subtitle="Company-level rollup of the same cohort. Activation uses meeting-owner attribution so support staff producing summaries on behalf of advisers still credit the adviser."
        filterPills={pills}
      />
      <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ color: FG3, fontSize: 12 }}>
          {fmtN(base.total)} cohort companies, of which {fmtN(base.multiSeat)} have 2+ active seats.
          {cmp && ` Comparison: ${fmtN(cmp.total)} (${fmtN(cmp.multiSeat)} multi-seat).`}
        </div>
        <div
          style={{
            padding: "8px 12px",
            border: "1px solid var(--border-default)",
            borderLeft: "4px solid var(--fg-3)",
            borderRadius: 6,
            background: "var(--bg-2)",
            fontSize: 12,
            color: "var(--fg-2)",
            lineHeight: 1.5,
          }}
        >
          {FIRM_SIZE_CAVEAT}
        </div>
        <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
          <KpiCard
            label="Teammate joined in 14d"
            info={TEAMMATE_14D_INFO}
            value={fmtPct(base.teammateIn14d, base.total)}
            sub={`${fmtN(base.teammateIn14d)} / ${fmtN(base.total)} companies`}
            compareValue={teammate14dPctCmp != null ? fmtPct(cmp!.teammateIn14d, cmp!.total) : undefined}
            compareSub={cmp ? `${fmtN(cmp.teammateIn14d)} / ${fmtN(cmp.total)}` : undefined}
            compareDeltaPp={teammate14dPctCmp != null ? teammate14dPctCmp - teammate14dPct : null}
          />
          <KpiCard
            label="Teammate joined in 30d"
            info={TEAMMATE_30D_INFO}
            value={fmtPct(base.teammateIn30d, base.total)}
            sub={`${fmtN(base.teammateIn30d)} / ${fmtN(base.total)} companies`}
            compareValue={teammate30dPctCmp != null ? fmtPct(cmp!.teammateIn30d, cmp!.total) : undefined}
            compareSub={cmp ? `${fmtN(cmp.teammateIn30d)} / ${fmtN(cmp.total)}` : undefined}
            compareDeltaPp={teammate30dPctCmp != null ? teammate30dPctCmp - teammate30dPct : null}
          />
          <KpiCard
            label="Teammate ever joined"
            info={TEAMMATE_EVER_INFO}
            value={fmtPct(base.hasPostFounderTeammate, base.total)}
            sub={`${fmtN(base.hasPostFounderTeammate)} / ${fmtN(base.total)} companies`}
            compareValue={teammateEverPctCmp != null ? fmtPct(cmp!.hasPostFounderTeammate, cmp!.total) : undefined}
            compareSub={cmp ? `${fmtN(cmp.hasPostFounderTeammate)} / ${fmtN(cmp.total)}` : undefined}
            compareDeltaPp={teammateEverPctCmp != null ? teammateEverPctCmp - teammateEverPct : null}
          />
        </div>
        <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
          <KpiCard
            label="Team activation rate"
            info={TEAM_ACTIVATION_INFO}
            value={fmtPct(base.multiSeatTeamActivated, base.multiSeat)}
            sub={`${fmtN(base.multiSeatTeamActivated)} / ${fmtN(base.multiSeat)} multi-seat firms`}
            compareValue={teamActPctCmp != null ? fmtPct(cmp!.multiSeatTeamActivated, cmp!.multiSeat) : undefined}
            compareSub={cmp ? `${fmtN(cmp.multiSeatTeamActivated)} / ${fmtN(cmp.multiSeat)}` : undefined}
            compareDeltaPp={teamActPctCmp != null ? teamActPctCmp - teamActPct : null}
          />
          <KpiCard
            label="Founder pulls in a teammate"
            info={FOUNDER_PULL_INFO}
            value={fmtPct(base.founderActivatedHasTeammate, base.founderActivated)}
            sub={`${fmtN(base.founderActivatedHasTeammate)} / ${fmtN(base.founderActivated)} activated founders`}
            compareValue={founderPullPctCmp != null ? fmtPct(cmp!.founderActivatedHasTeammate, cmp!.founderActivated) : undefined}
            compareSub={cmp ? `${fmtN(cmp.founderActivatedHasTeammate)} / ${fmtN(cmp.founderActivated)}` : undefined}
            compareDeltaPp={founderPullPctCmp != null ? founderPullPctCmp - founderPullPct : null}
          />
        </div>
      </div>
    </section>
  );
}
